// Mail client — the /mail inbox's data layer. Speaks JMAP (RFC 8620/8621)
// to the kit backend's proxy (api/mailApi.js), never to the mail server:
// the backend injects the mailbox credential and rewrites every URL in the
// session to its own /mail/* routes. Sending is a dedicated POST /mail/send
// (caps + pause live there), everything else is plain JMAP method calls.
import { getIdToken } from './authPlane';
import { kitEnv } from './kitEnv';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');
export const USING = ['urn:ietf:params:jmap:core', 'urn:ietf:params:jmap:mail'];

export async function mailApi(path, body, method) {
  const token = await getIdToken();
  const res = await fetch(`${API}${path}`, {
    method: method || (body === undefined ? 'GET' : 'POST'),
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(json.error || `${path}: ${res.status}`); e.status = res.status; e.outcome = json.outcome; throw e; }
  return json;
}

// Module-level cache so the navbar can show the Mail link without every
// page re-asking.
let _status = null;
let _statusAt = 0;
export async function mailStatus({ fresh = false, unread = false } = {}) {
  if (_status && !fresh && !unread && Date.now() - _statusAt < 60e3) return _status;
  const s = await mailApi(unread ? '/mail/status?unread=1' : '/mail/status');
  _status = s;
  _statusAt = Date.now();
  return s;
}
export const getVacation = (localPart) => mailApi(`/mail/vacation/${encodeURIComponent(localPart)}`);
export const setVacation = (localPart, v) => mailApi(`/mail/vacation/${encodeURIComponent(localPart)}`, v);
export const invalidateMailStatus = () => { _status = null; };

export const LIST_PROPS = ['id', 'threadId', 'mailboxIds', 'keywords', 'from', 'to', 'subject', 'receivedAt', 'size', 'preview', 'hasAttachment'];
export const FULL_PROPS = [
  'id', 'blobId', 'threadId', 'mailboxIds', 'keywords', 'size', 'receivedAt', 'messageId', 'inReplyTo', 'references',
  'from', 'to', 'cc', 'bcc', 'replyTo', 'subject', 'sentAt', 'hasAttachment', 'preview', 'bodyValues', 'textBody', 'htmlBody', 'attachments',
];

export class MailClient {
  constructor(session) {
    this.session = session;
    this.accountId = session.accountId;
    this.localPart = session.localPart;
    this.address = session.address;
  }

  static async open(localPart) {
    const session = await mailApi('/mail/open', localPart ? { localPart } : {});
    return new MailClient(session);
  }

  async call(methodCalls, using = USING) {
    const out = await mailApi(this.session.apiUrl, { using, methodCalls });
    const errors = (out.methodResponses || []).filter((r) => r[0] === 'error');
    if (errors.length && errors.length === out.methodResponses.length) {
      throw new Error(errors[0][1]?.description || errors[0][1]?.type || 'mail server error');
    }
    return out.methodResponses || [];
  }
  pick(responses, tag) { return responses.find((r) => r[2] === tag)?.[1] || {}; }

  // ---- folders / labels -------------------------------------------------------
  async mailboxes() {
    const r = await this.call([['Mailbox/get', { accountId: this.accountId, ids: null }, 'm']]);
    const list = this.pick(r, 'm').list || [];
    const order = { inbox: 0, drafts: 1, sent: 2, archive: 3, junk: 4, trash: 5 };
    return list.sort((a, b) => (order[a.role] ?? 10) - (order[b.role] ?? 10) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
  }
  async createMailbox(name, parentId = null) {
    const r = await this.call([['Mailbox/set', { accountId: this.accountId, create: { n: { name, parentId } } }, 'c']]);
    const s = this.pick(r, 'c');
    if (s.notCreated?.n) throw new Error(s.notCreated.n.description || 'could not create label');
    return s.created?.n;
  }
  async renameMailbox(id, name) {
    await this.call([['Mailbox/set', { accountId: this.accountId, update: { [id]: { name } } }, 'u']]);
  }
  async destroyMailbox(id) {
    const r = await this.call([['Mailbox/set', { accountId: this.accountId, destroy: [id], onDestroyRemoveEmails: false }, 'd']]);
    const s = this.pick(r, 'd');
    if (s.notDestroyed?.[id]) throw new Error(s.notDestroyed[id].description || 'label is not empty');
  }

  // ---- list ---------------------------------------------------------------------
  // filter: { inMailbox, text, from, notKeyword, hasKeyword, hasAttachment, after }
  async query({ filter = {}, position = 0, limit = 50, anchor = null } = {}) {
    const r = await this.call([
      ['Email/query', {
        accountId: this.accountId, filter, sort: [{ property: 'receivedAt', isAscending: false }],
        collapseThreads: true, position, limit, calculateTotal: true, ...(anchor ? { anchor, anchorOffset: 1 } : {}),
      }, 'q'],
      ['Email/get', { accountId: this.accountId, '#ids': { resultOf: 'q', name: 'Email/query', path: '/ids' }, properties: LIST_PROPS }, 'g'],
    ]);
    const q = this.pick(r, 'q');
    const g = this.pick(r, 'g');
    if (q.type) throw new Error(q.description || q.type);
    return { ids: q.ids || [], total: q.total ?? null, position: q.position || 0, list: g.list || [], queryState: q.queryState };
  }

  // Thread → every email in it, full bodies.
  async thread(threadId) {
    const r = await this.call([
      ['Thread/get', { accountId: this.accountId, ids: [threadId] }, 't'],
      ['Email/get', {
        accountId: this.accountId, '#ids': { resultOf: 't', name: 'Thread/get', path: '/list/*/emailIds' },
        properties: FULL_PROPS, fetchTextBodyValues: true, fetchHTMLBodyValues: true, maxBodyValueBytes: 800000,
      }, 'e'],
    ]);
    const list = this.pick(r, 'e').list || [];
    return list.sort((a, b) => new Date(a.receivedAt) - new Date(b.receivedAt));
  }

  async email(id) {
    const r = await this.call([['Email/get', { accountId: this.accountId, ids: [id], properties: FULL_PROPS, fetchTextBodyValues: true, fetchHTMLBodyValues: true, maxBodyValueBytes: 800000 }, 'e']]);
    return (this.pick(r, 'e').list || [])[0] || null;
  }

  // ---- mutations (Email/set is proxied; EmailSubmission/set is NOT) -----------
  async update(ids, patch) {
    const update = Object.fromEntries((Array.isArray(ids) ? ids : [ids]).map((id) => [id, patch]));
    const r = await this.call([['Email/set', { accountId: this.accountId, update }, 'u']]);
    const s = this.pick(r, 'u');
    const bad = Object.values(s.notUpdated || {})[0];
    if (bad) throw new Error(bad.description || bad.type);
  }
  markSeen(ids, seen = true) { return this.update(ids, { 'keywords/$seen': seen ? true : null }); }
  flag(ids, on = true) { return this.update(ids, { 'keywords/$flagged': on ? true : null }); }
  move(ids, fromId, toId) { return this.update(ids, { ...(fromId ? { [`mailboxIds/${fromId}`]: null } : {}), [`mailboxIds/${toId}`]: true }); }
  label(ids, labelId, on = true) { return this.update(ids, { [`mailboxIds/${labelId}`]: on ? true : null }); }
  async destroy(ids) {
    const r = await this.call([['Email/set', { accountId: this.accountId, destroy: Array.isArray(ids) ? ids : [ids] }, 'd']]);
    const s = this.pick(r, 'd');
    const bad = Object.values(s.notDestroyed || {})[0];
    if (bad) throw new Error(bad.description || bad.type);
  }

  // Draft: create (or replace) an Email in the Drafts mailbox.
  async saveDraft(draftsId, { to = [], cc = [], bcc = [], subject = '', text = '', inReplyTo, references, attachments = [], replaceId = null }) {
    const email = {
      mailboxIds: { [draftsId]: true }, keywords: { $draft: true, $seen: true },
      from: [{ email: this.address }], to, cc, bcc, subject,
      ...(inReplyTo ? { inReplyTo: [inReplyTo] } : {}), ...(references?.length ? { references } : {}),
      bodyValues: { t: { value: text, charset: 'utf-8' } }, textBody: [{ partId: 't', type: 'text/plain' }],
      ...(attachments.length ? { attachments: attachments.map((a) => ({ blobId: a.blobId, type: a.type, name: a.name, disposition: 'attachment' })) } : {}),
    };
    const r = await this.call([['Email/set', { accountId: this.accountId, create: { d: email }, ...(replaceId ? { destroy: [replaceId] } : {}) }, 's']]);
    const s = this.pick(r, 's');
    if (s.notCreated?.d) throw new Error(s.notCreated.d.description || 'could not save draft');
    return s.created?.d?.id || null;
  }

  // ---- blobs ----------------------------------------------------------------------
  blobUrl(blobId, name = 'attachment', type = 'application/octet-stream', { inline = false } = {}) {
    const path = this.session.downloadUrl
      .replace('{accountId}', encodeURIComponent(this.accountId))
      .replace('{blobId}', encodeURIComponent(blobId))
      .replace('{name}', encodeURIComponent(name))
      .replace('{type}', encodeURIComponent(type));
    return `${API}${path}${inline ? '&inline=1' : ''}`;
  }
  // Authenticated fetch → object URL (the download route needs the bearer).
  async blobObjectUrl(blobId, name, type) {
    const token = await getIdToken();
    const res = await fetch(this.blobUrl(blobId, name, type, { inline: true }), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`attachment ${res.status}`);
    return URL.createObjectURL(await res.blob());
  }
  async upload(file) {
    const token = await getIdToken();
    const path = this.session.uploadUrl.replace('{accountId}', encodeURIComponent(this.accountId));
    const res = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': file.type || 'application/octet-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: file });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `upload ${res.status}`);
    return { blobId: json.blobId, type: json.type || file.type || 'application/octet-stream', size: json.size || file.size, name: file.name };
  }

  // ---- send (caps + pause enforced server-side) -----------------------------------
  send(payload) { return mailApi('/mail/send', { from: this.localPart, ...payload }); }

  // ---- push: JMAP EventSource through the proxy -----------------------------------
  // onChange(changedTypes: string[]) fires on every state event. Returns stop().
  events(onChange) {
    let es = null; let stopped = false; let backoff = 2000;
    const connect = async () => {
      if (stopped) return;
      const token = await getIdToken();
      if (!token) { setTimeout(connect, 5000); return; }
      es = new EventSource(`${API}${this.session.eventSourceUrl}?token=${encodeURIComponent(token)}&types=Email,Mailbox,Thread`);
      es.addEventListener('state', (ev) => {
        backoff = 2000;
        try {
          const data = JSON.parse(ev.data || '{}');
          const changed = Object.values(data.changed || {}).flatMap((m) => Object.keys(m || {}));
          onChange(changed.length ? changed : ['Email']);
        } catch { onChange(['Email']); }
      });
      es.onerror = () => {
        es?.close(); es = null;
        if (!stopped) { setTimeout(connect, backoff); backoff = Math.min(backoff * 2, 60000); }
      };
    };
    connect();
    return () => { stopped = true; es?.close(); };
  }
}

// ---- helpers shared by the UI ------------------------------------------------------
export const addrName = (a) => (a?.name ? a.name : (a?.email || '').split('@')[0]);
export const addrFull = (a) => (a?.name ? `${a.name} <${a.email}>` : (a?.email || ''));
export const parseAddresses = (s) => String(s || '').split(/[,;\n]+/).map((x) => x.trim()).filter(Boolean).map((x) => {
  const m = x.match(/^(.*?)\s*<([^>]+)>$/);
  return m ? { name: m[1].replace(/^"|"$/g, '').trim(), email: m[2].trim().toLowerCase() } : { email: x.toLowerCase() };
});
export const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n > 1024 ? `${Math.round(n / 1024)} KB` : `${n || 0} B`);
export function fmtDate(iso, { long = false } = {}) {
  if (!iso) return '';
  const d = new Date(iso);
  if (long) return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Body of one email: prefer html (sanitized elsewhere), else text.
export function bodyOf(email) {
  const val = (parts) => (parts || []).map((p) => email.bodyValues?.[p.partId]?.value || '').join('\n');
  const html = val(email.htmlBody);
  const text = val(email.textBody);
  return { html: html && html.trim() ? html : null, text };
}

// Quote a message for reply/forward (plain text).
export function quoteText(email) {
  const { text, html } = bodyOf(email);
  const src = text || (html ? html.replace(/<style[\s\S]*?<\/style>/gi, '').replace(/<[^>]+>/g, ' ').replace(/\s+\n/g, '\n').replace(/[ \t]+/g, ' ').trim() : '');
  return src.split('\n').map((l) => `> ${l}`).join('\n');
}

// HTML sanitization for the sandboxed iframe: scripts out, inline handlers
// out, remote loads blocked by CSP unless `remote` is true; cid: images are
// resolved to blob urls the caller already fetched.
export function sanitizeHtml(html, { remote = false, cidMap = {} } = {}) {
  let out = String(html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
    .replace(/<(object|embed|applet|form|base|meta)[^>]*>/gi, '')
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|src)\s*=\s*("|')\s*javascript:[^"']*\2/gi, '$1="#"')
    .replace(/src\s*=\s*("|')cid:([^"']+)\1/gi, (_, q, cid) => `src="${cidMap[cid] || cidMap[`<${cid}>`] || ''}"`);
  const csp = `default-src 'none'; img-src ${remote ? '* data: blob:' : 'data: blob:'}; style-src 'unsafe-inline'; font-src ${remote ? '*' : "'none'"};`;
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><base target="_blank"><style>body{margin:0;padding:12px;font:14px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;color:#1a1a1a;background:#fff;word-wrap:break-word}img{max-width:100%;height:auto}blockquote{border-left:3px solid #ccc;margin:8px 0;padding-left:12px;color:#555}pre{white-space:pre-wrap}</style></head><body>${out}</body></html>`;
}
